import { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { Server, ExternalLink, Copy, Eye, EyeOff, CheckCircle2 } from 'lucide-react';
import { Modal } from './Modal';
import { OrdersAPI } from '../api';

interface CpanelAccessModalProps {
  open: boolean;
  onClose: () => void;
  order: any | null;
}

export function CpanelAccessModal({ open, onClose, order }: CpanelAccessModalProps) {
  const { getToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [creds, setCreds] = useState<{ url: string; user: string; pass: string } | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  useEffect(() => {
    if (open && order) {
      setLoading(true);
      setError(null);
      setCreds(null);
      setShowPassword(false);
      setCopiedField(null);

      const fetchCredentials = async () => {
        try {
          const token = await getToken();
          if (!token) {
            setError('Error de autenticación');
            return;
          }
          
          const res = await OrdersAPI.getCpanelCredentials(order.id, token);
          if (res.success && res.data) {
            setCreds({
              url: res.data.cpanelUrl || '',
              user: res.data.cpanelUser || '',
              pass: res.data.cpanelPassword || ''
            });
          } else {
            setError(res.error || 'No se pudieron recuperar las credenciales');
          }
        } catch (err: any) {
          setError(err.message || 'Error desconocido');
        } finally {
          setLoading(false);
        }
      };

      fetchCredentials();
    }
  }, [open, order, getToken]);

  const handleCopy = (text: string, field: string) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const getServiceName = () => {
    if (!order) return '';
    const svc = order.hosting || order.complement;
    return svc?.name || svc?.displayName || '';
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      icon={<Server size={22} />}
      title="Accesos cPanel"
      subtitle={getServiceName()}
      footer={
        <button
          onClick={onClose}
          className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md dark:text-gray-300 dark:hover:bg-slate-700"
        >
          Cerrar
        </button>
      }
    >
      {loading ? (
        <div className="py-8 flex flex-col items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-sm text-gray-500 dark:text-gray-400">Desencritpando accesos seguros...</p>
        </div>
      ) : error ? (
        <div className="p-4 bg-red-50 text-red-700 rounded-md border border-red-200 dark:bg-red-900/20 dark:border-red-900 dark:text-red-200">
          <p className="font-medium text-sm">Error</p>
          <p className="text-sm">{error}</p>
        </div>
      ) : creds ? (
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Utiliza estas credenciales para ingresar a tu panel de control de hosting.
          </p>

          <div className="space-y-3">
            {/* URL Field */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 dark:text-gray-400">
                URL del Panel
              </label>
              <div className="flex gap-2">
                <div className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-800 break-all dark:bg-slate-900/50 dark:border-slate-700 dark:text-gray-200">
                  {creds.url || 'No configurada'}
                </div>
                {creds.url && (
                  <a
                    href={creds.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center px-4 py-2 bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 transition-colors dark:bg-blue-900/30 dark:text-blue-400 dark:hover:bg-blue-900/50"
                  >
                    <ExternalLink size={18} />
                  </a>
                )}
              </div>
            </div>

            {/* User Field */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 dark:text-gray-400">
                Usuario
              </label>
              <div className="flex gap-2">
                <div className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm font-mono text-gray-800 dark:bg-slate-900/50 dark:border-slate-700 dark:text-gray-200">
                  {creds.user || 'No configurado'}
                </div>
                {creds.user && (
                  <button
                    onClick={() => handleCopy(creds.user, 'user')}
                    className="flex flex-col items-center justify-center px-4 py-2 bg-gray-100 text-gray-600 rounded-md hover:bg-gray-200 transition-colors w-14 dark:bg-slate-700 dark:text-gray-300 dark:hover:bg-slate-600"
                  >
                    {copiedField === 'user' ? <CheckCircle2 size={18} className="text-green-600 dark:text-green-400" /> : <Copy size={18} />}
                  </button>
                )}
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 dark:text-gray-400">
                Contraseña
              </label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    readOnly
                    value={creds.pass || 'Sin contraseña'}
                    className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm font-mono text-gray-800 dark:bg-slate-900/50 dark:border-slate-700 dark:text-gray-200 pr-10"
                  />
                  {creds.pass && (
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 p-1"
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  )}
                </div>
                {creds.pass && (
                  <button
                    onClick={() => handleCopy(creds.pass, 'pass')}
                    className="flex flex-col items-center justify-center px-4 py-2 bg-gray-100 text-gray-600 rounded-md hover:bg-gray-200 transition-colors w-14 dark:bg-slate-700 dark:text-gray-300 dark:hover:bg-slate-600"
                  >
                    {copiedField === 'pass' ? <CheckCircle2 size={18} className="text-green-600 dark:text-green-400" /> : <Copy size={18} />}
                  </button>
                )}
              </div>
            </div>
          </div>
          <div className="p-3 bg-amber-50 text-amber-800 rounded-md flex items-start gap-2 text-xs border border-amber-100 dark:bg-amber-900/20 dark:border-amber-900/50 dark:text-amber-200">
            <span className="text-lg">⚠</span>
            <p className="mt-0.5">
              Mantén estas credenciales en un lugar seguro. Con ellas tienes acceso total a los archivos de tu servidor.
            </p>
          </div>
        </div>
      ) : null}
    </Modal>
  );
}
